import { useCallback } from 'react';
import { useRecoilValue } from 'recoil';
import type { IRowNode } from 'ag-grid-community';
import { portfolioStrategyFilterType } from 'venn-state';
import { useBlockId } from '../contexts/BlockIdContext';
import { PortfolioStrategyFilterType } from '../components/grid/types';
import { assertExhaustive } from 'venn-utils';
import { usePortfolioMultiFiltering } from '../../blocks/utils/hooks/use-portfolio-multi-filtering';

export const shouldNodeExpand = (
  strategyFilterType: PortfolioStrategyFilterType,
  rowNode: IRowNode<unknown>,
): boolean => {
  switch (strategyFilterType) {
    case PortfolioStrategyFilterType.DEFAULT:
      return true;
    case PortfolioStrategyFilterType.TOP_LEVEL_STRATEGIES:
      // If we want to show only the top level, we don't expand any nodes. Expanding a node inherently shows things that aren't top-level.
      return rowNode.level < 0;
    case PortfolioStrategyFilterType.ALL_STRATEGIES:
      return (rowNode.childrenAfterGroup ?? []).some((child) => !!child.allChildrenCount);
    case PortfolioStrategyFilterType.CUSTOM_SELECTION:
      return false;
    default:
      throw assertExhaustive(strategyFilterType);
  }
};

export const usePortfolioGridDisplaySettings = (rowData: unknown[], columnDefs: unknown[]) => {
  const blockId = useBlockId();
  const strategyFilterType = useRecoilValue(portfolioStrategyFilterType(blockId));
  const { hasMultiLevelPortfolioFiltering } = usePortfolioMultiFiltering(blockId);

  return useCallback(
    (rowNode: IRowNode<unknown>) => {
      if (hasMultiLevelPortfolioFiltering) {
        return true;
      }
      return shouldNodeExpand(strategyFilterType ?? PortfolioStrategyFilterType.DEFAULT, rowNode);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `rowData` and `columnDef` to make expansion work after re-renders.
    [strategyFilterType, hasMultiLevelPortfolioFiltering, rowData, columnDefs],
  );
};
